import React from 'react';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Modal,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import {
  CloseRounded,
  FilterList as FilterListIcon,
  SvgIconComponent,
} from '@mui/icons-material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Hit } from 'algoliasearch';
import { GroupedVirtuoso } from 'react-virtuoso';
import useElementHeight from '../../../hooks/ui/useHeightRef';
import SearchInput from '../../form/SearchInput';
import { FilterOption } from '../../../interfaces';
import { important } from '../../../utils/global/helper';
import DropdownMenu from '../DropdownMenu';
import SearchModalRow from './SearchModalRow';

const style: SxProps<Theme> = {
  bgcolor: 'background.paper',
  overflowY: 'auto',
  pointerEvents: 'auto',
  boxShadow: '0px 0px 10px 0px #E5E5E580',
};

const BREAKPOINT = 'md';

export interface SearchItemButton<T extends any = any> {
  onClick: (item: { type: string; item: Hit<T> }) => void | Promise<void>;
  content: React.ReactNode;
}

interface Result {
  type: string;
  id: string;
  href?: string;
  icon?: SvgIconComponent;
  title: string;
  details?: string[];
  itemButton?: SearchItemButton | null;
  onClick?: () => void;
}

type Data = {
  title: string;
  results: Result[];
  addAction?: {
    icon: SvgIconComponent;
    label: string;
    onClick: () => void;
  };
};

export interface Props {
  open: boolean;
  toggleOpen: () => void;
  placeholder: string;
  isLoading?: boolean;
  renderLoader?: boolean;
  onChange: (text: string) => void;
  data: Data[];
  filter?: {
    options: FilterOption[];
    selected: string | null;
    onChange: (value: string | null) => void;
    placeholder: string;
    /**
     * Do not show badge is one of these values is selected
     */
    defaultValues?: string[];
  };
}

function SearchModal({
  open,
  toggleOpen,
  placeholder,
  isLoading,
  renderLoader,
  onChange,
  data,
  filter,
}: Props) {
  const [text, setText] = React.useState<string>('');

  const { ref: containerRef, height } = useElementHeight();

  const hasNoData = React.useMemo(
    () => !data?.length || data.every(d => !d.results.length && !d.addAction),
    [data],
  );
  const showLoading = isLoading && renderLoader;

  const groups = React.useMemo(
    () => data.filter(d => d.results.length || d.addAction),
    [data],
  );

  const groupCounts = React.useMemo(
    () => groups.map(group => group.results.length + (group.addAction ? 1 : 0)),
    [groups],
  );

  const flatResults = React.useMemo(
    () =>
      groups.flatMap(g => {
        const groupResults = [...g.results];
        if (g.addAction) {
          groupResults.push({
            id: 'add-action',
            type: 'add-action',
            title: g.addAction.label,
            icon: g.addAction.icon,
            onClick: g.addAction.onClick,
          });
        }
        return groupResults;
      }),
    [groups],
  );

  const renderItem = React.useCallback(
    (index: number) => {
      const item = flatResults[index]!;
      if (!item) return null;

      const {
        id,
        type,
        icon: Icon,
        title: resultTitle,
        details,
        href,
        itemButton,
        onClick,
      } = item;
      if (!id) return null;

      const onItemButtonClick = () => {
        itemButton?.onClick({
          type,
          item,
        });
      };

      return (
        <SearchModalRow
          id={id}
          title={resultTitle}
          details={details}
          button={
            itemButton
              ? {
                  content: itemButton.content,
                  onClick: onItemButtonClick,
                }
              : undefined
          }
          toggleOpenModal={toggleOpen}
          href={href}
          icon={Icon}
          onClick={onClick}
        />
      );
    },
    [flatResults, toggleOpen],
  );

  const renderGroupContent = React.useCallback(
    (index: number) => {
      const { title, results, addAction } = groups[index]!;
      if (!results.length && !addAction) return null;

      return (
        <Typography
          variant="body1"
          color="text.secondary"
          pb={2}
          mt={index > 0 ? 2.5 : 0}
          bgcolor="background.paper">
          {title}
        </Typography>
      );
    },
    [groups],
  );

  if (!open) return null;

  return (
    <Modal
      closeAfterTransition
      open={open}
      onClose={toggleOpen}
      disableRestoreFocus>
      <Box
        boxSizing="border-box"
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        pt={{
          xs: 0,
          [BREAKPOINT]: 5,
        }}
        pb={{
          xs: 0,
          [BREAKPOINT]: 4,
        }}
        sx={{
          pointerEvents: 'none',
        }}>
        <Box
          boxSizing="border-box"
          m="auto"
          boxShadow={24}
          width="100vw"
          maxWidth={{
            xs: 'unset',
            [BREAKPOINT]: 'min(1040px, calc(100% - 32px))',
          }}
          display="flex"
          flexDirection="column"
          height={text?.length ? '100%' : 'auto'}
          overflow="hidden"
          borderRadius={{
            xs: 0,
            [BREAKPOINT]: '10px',
          }}
          sx={style}>
          <Box
            display="flex"
            alignItems="stretch"
            sx={{
              position: 'sticky',
              zIndex: 2,
              top: 0,
              borderBottom: text?.length ? `1px solid #E5E5E580` : 'none',
              boxShadow: text?.length ? '0px 0px 10px 0px #E5E5E580' : 'none',
            }}>
            {filter && (
              <DropdownMenu
                button={{
                  content: (
                    <Badge
                      color="primary"
                      variant="dot"
                      invisible={
                        !filter?.selected ||
                        filter.defaultValues?.includes(filter.selected)
                      }>
                      <FilterListIcon
                        htmlColor="black"
                        sx={{
                          fontSize: '24px',
                        }}
                      />
                    </Badge>
                  ),
                  sx: {
                    borderRadius: '0',
                    width: {
                      xs: important('50px'),
                      [BREAKPOINT]: important('60px'),
                    },
                    order: {
                      xs: 0,
                      [BREAKPOINT]: 2,
                    },
                  },
                }}
                options={filter.options.map(option => ({
                  id: option.value,
                  value: option.value,
                  label: option.label,
                  action: () => filter.onChange(option.value),
                }))}
                selectedOption={filter.selected || ''}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
              />
            )}

            <SearchInput
              mobileHideIcon
              autoFocus
              value={text}
              onChange={val => {
                setText(val);
                onChange(val);
              }}
              placeholder={placeholder}
              endAdornment={
                isLoading ? (
                  <Box
                    flexShrink={0}
                    width="32px"
                    height="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center">
                    <CircularProgress size={20} color="primary" />
                  </Box>
                ) : (
                  <Button
                    sx={{
                      flexShrink: 0,
                      height: '100%',
                      display: {
                        xs: 'flex',
                        [BREAKPOINT]: 'none',
                      },
                      alignItems: 'center',
                      minWidth: 0,
                      p: 1,
                    }}>
                    <CloseRounded
                      sx={{
                        width: '24px',
                        color: 'text.secondary',
                      }}
                      onClick={toggleOpen}
                    />
                  </Button>
                )
              }
            />
          </Box>

          {!!text?.length && (
            <Box
              boxSizing="border-box"
              py={2}
              px={4}
              zIndex={1}
              position="relative"
              width="100%"
              height="100%">
              {!showLoading &&
                (hasNoData ? (
                  <Stack
                    width="100%"
                    m="auto"
                    alignItems="center"
                    gap={2}
                    boxSizing="border-box">
                    <SearchOffIcon
                      fontSize="large"
                      sx={{
                        fontSize: '64px',
                      }}
                    />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                      No results for{' '}
                      <Typography
                        variant="inherit"
                        component="span"
                        fontWeight={700}
                        sx={{
                          lineBreak: 'anywhere',
                        }}>
                        &quot;{text}&quot;
                      </Typography>
                    </Typography>
                  </Stack>
                ) : (
                  <Box height="100%" flexGrow={1} ref={containerRef}>
                    <GroupedVirtuoso
                      groupCounts={groupCounts}
                      groupContent={renderGroupContent}
                      style={{
                        width: '100%',
                        height: height ? `${height}px` : '100%',
                      }}
                      itemContent={renderItem}
                    />
                  </Box>
                ))}
              {showLoading && (
                <Box
                  mt={2}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="100%">
                  <CircularProgress />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}

export default SearchModal;
